import React from "react";
import {
  Stethoscope,
  Hospital,
  BadgePercent,
  BriefcaseMedical,
  ShieldCheck,
  Users,
  ArrowRight,
  CircleCheckBig,
} from "lucide-react";
import doctor from "../../assets/doctor-handshake.png"
import { motion } from "framer-motion";

const Provider = () => {
  const providerBenefits = [
    {
      title: "Special Offers",
      description:
        "Promote exclusive healthcare offers for corporate employees.",
      icon: <BadgePercent size={24} className="text-royalBlue" strokeWidth={1.5} />,
    },
    {
      title: "Corporate Discounts",
      description:
        "Provide employee-focused discounts and improve patient reach.",
      icon: <Users size={24} className="text-royalGreen" strokeWidth={1.5} />,
    },
    {
      title: "Healthcare Packages",
      description:
        "Showcase preventive care and wellness packages to organizations.",
      icon: <BriefcaseMedical size={24} className="text-royalBlue" strokeWidth={1.5} />,
    },
    {
      title: "Clinic & Hospital Visibility",
      description:
        "Increase visibility for your healthcare services through Livmo.",
      icon: <Hospital size={24} className="text-royalGreen" strokeWidth={1.5} />,
    },
  ];

  return (
    <motion.section
      id="ProviderProgram"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.8,
        ease: "easeOut"
      }}
      viewport={{ once: true, amount: 0.2 }}
      className="relative overflow-hidden bg-gradient-to-b from-secondary to-secondary/80 py-[6vw] lg:py-[3vw]"
    >
      {/* Background Glow - responsive */}
      <div className="absolute top-[-10%] right-[-5%] w-[200px] h-[200px] lg:w-[350px] lg:h-[350px] bg-royalBlue/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-[-10%] left-[-5%] w-[200px] h-[200px] lg:w-[350px] lg:h-[350px] bg-royalGreen/5 rounded-full blur-3xl"></div>

      <div className="max-w-[1450px] mx-auto px-[5%] relative z-10">

        {/* Main Card */}
        <div className="bg-white border border-gray-100 rounded-2xl lg:rounded-[2vw] shadow-2xl hover:shadow-[0_20px_80px_rgba(65,105,225,0.12)] transition-all duration-500 overflow-hidden">

          <div className="grid lg:grid-cols-2 gap-0">

            {/* LEFT CONTENT */}
            <div className="p-6 sm:p-8 lg:p-12 flex flex-col justify-center">

              {/* Pill Header - Copy this block, change the text only */}
              <div className="flex justify-center mb-8 md:mb-10 px-4 sm:px-0">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-royalBlue/10 to-royalBlue/5 backdrop-blur-sm rounded-full px-5 py-2 sm:px-6 md:px-7 sm:py-2 md:py-3 border border-royalBlue/20 shadow-lg max-w-full">
                  <ShieldCheck size={18} className="text-royalGreen sm:size-[18px] flex-shrink-0" />
                  <p className="font-poppins font-semibold text-royalBlue text-sm sm:text-base md:text-lg tracking-wide uppercase text-center leading-tight">
                    PROVIDER VISIBILITY PROGRAM
                  </p>
                </div>
              </div>

              {/* Heading - improved */}
              <h2 className="mt-2.5 lg:mt-3.25 font-poppins font-[700] text-darkblue leading-[1.25] text-[7vw] sm:text-[5.5vw] md:text-[3.5vw] lg:text-[2.8vw] bg-gradient-to-r from-darkblue to-primary bg-clip-text text-transparent">
                Livmo Health Corporate Visibility Program for Healthcare Providers
              </h2>

              {/* Description */}
              <p className="mt-2 lg:mt-3 text-text1/90 leading-[1.7] font-poppins text-[3.5vw] sm:text-[2.2vw] md:text-[1.3vw] lg:text-[0.95vw]">
                Partner with Livmo to directly connect with corporate employees
                looking for trusted healthcare services. Increase your provider
                visibility and showcase your healthcare offerings through a
                reliable healthcare coordination platform.
              </p>

              {/* Trust Points - perfectly aligned across all screens */}
              <div className="mt-4 lg:mt-6 space-y-4 lg:space-y-5">
                {[
                  "Connect with corporate employees across India",
                  "Increase healthcare service visibility",
                  "Promote trusted provider offerings",
                  "Strengthen long-term patient relationships",
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 lg:gap-4 group"
                  >
                    {/* Icon container height matches text line height */}
                    <div className="flex-shrink-0 w-6 h-6 lg:w-7 lg:h-7 rounded-full bg-gradient-to-br from-royalGreen/10 to-royalGreen/5 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <CircleCheckBig
                        size={14}
                        className="text-royalGreen lg:size-[16px]"
                      />
                    </div>

                    {/* Text with line height that matches icon container height */}
                    <p className="text-darkblue/90 font-poppins font-medium text-sm lg:text-base leading-6 lg:leading-7 group-hover:text-darkblue transition-colors duration-300 flex-1">
                      {item}
                    </p>
                  </div>
                ))}
              </div>

              {/* CTA - improved */}
              <div className="mt-3 lg:mt-4">
                <button className="group bg-gradient-to-r from-royalBlue to-royalGreen text-white rounded-xl px-5 py-2.5 md:px-6 md:py-3 font-poppins font-semibold shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-100 transition-all duration-300 flex items-center gap-2 text-sm md:text-base"
                onClick={() => (window.location.href = "https://app.livmohealth.com/provider/auth/sign-up")}  >
                  Become a Livmo Provider
                  <ArrowRight
                    size={16}
                    className="md:size-[18px] group-hover:translate-x-1 transition-transform duration-300"
                  />
                </button>
              </div>
            </div>

            {/* RIGHT VISUAL */}
            <div className="relative bg-gradient-to-br from-royalBlue/5 via-transparent to-royalGreen/5 min-h-[280px] md:min-h-[380px] lg:min-h-[420px] flex items-center justify-center overflow-hidden p-2 md:p-3 lg:p-4">

              {/* Decorative Glow (reduced size on mobile) */}
              <div className="absolute top-[10%] right-[8%] w-[80px] h-[80px] md:w-[140px] md:h-[140px] lg:w-[180px] lg:h-[180px] bg-royalBlue/10 rounded-full blur-2xl lg:blur-3xl"></div>
              <div className="absolute bottom-[5%] left-[5%] w-[80px] h-[80px] md:w-[140px] md:h-[140px] lg:w-[180px] lg:h-[180px] bg-royalGreen/8 rounded-full blur-2xl lg:blur-3xl"></div>

              {/* Main Image - smaller on mobile */}
              <div className="relative z-10 w-[65%] sm:w-[55%] md:w-[70%] lg:w-[75%]">
                <img
                  src={doctor}
                  alt="Healthcare Provider Partnership"
                  className="w-full object-contain drop-shadow-xl rounded-lg"
                />
              </div>

              {/* Floating Cards (unchanged, still hidden on mobile) */}
              <div className="absolute top-[8%] left-[4%] bg-white/95 backdrop-blur-sm border border-royalBlue/20 shadow-xl rounded-xl lg:rounded-2xl p-2 lg:p-4 w-[160px] lg:w-[220px] hidden lg:block hover:scale-105 transition-all duration-300">
                <div className="flex items-center gap-2 lg:gap-4">
                  <div className="w-9 h-9 lg:w-[50px] lg:h-[50px] rounded-xl bg-gradient-to-br from-royalBlue/10 to-royalBlue/5 flex items-center justify-center">
                    <Stethoscope size={18} className="text-royalBlue lg:size-[24px]" />
                  </div>
                  <div>
                    <h4 className="font-poppins font-bold text-darkblue text-xs lg:text-sm">Verified Providers</h4>
                    <p className="text-text1/80 font-poppins text-[10px] lg:text-xs mt-0.5 leading-tight">Build trust with corporate employees.</p>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Bottom Feature Grid - IMPROVED CARDS */}
          <div className="border-t border-gray-100 bg-gradient-to-b from-white to-secondary/30 px-4 sm:px-6 py-6 sm:py-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {providerBenefits.map((benefit, index) => (
                <div
                  key={index}
                  className="group bg-white border border-gray-200 hover:border-royalBlue/30 rounded-xl p-4 md:p-5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-11 h-11 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-royalBlue/10 to-royalGreen/10 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                    {benefit.icon}
                  </div>

                  <h3 className="mt-3 md:mt-4 font-poppins font-bold text-darkblue leading-snug text-base md:text-lg group-hover:text-royalBlue transition-colors">
                    {benefit.title}
                  </h3>

                  <p className="mt-1.5 md:mt-2 text-text1/80 leading-relaxed font-poppins text-sm md:text-base">
                    {benefit.description}
                  </p>

                  <div className="mt-3 md:mt-4 w-8 h-0.5 rounded-full bg-gradient-to-r from-royalBlue to-royalGreen group-hover:w-12 transition-all duration-300"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default Provider;